<script lang="ts">
  import { slide } from 'svelte/transition';
  import { getSuggest } from '../../api/suggest';

  interface Props {
    participantId: string;
    maskName: string;
    initialName: string;
    onSubmit: (name: string) => void;
  }

  const { participantId, maskName, initialName, onSubmit }: Props = $props();

  const getStorageKey = () => `unmask-name-${participantId}`;

  const storeName = (name: string) => {
    try {
      if (name) {
        localStorage.setItem(getStorageKey(), name);
      } else {
        localStorage.removeItem(getStorageKey());
      }
    } catch {
      // ignore
    }
  };

  // svelte-ignore state_referenced_locally
  let name = $state(initialName);
  let suggestedName = $state<string | null>(null);
  let suggestionPending = $state(false);
  let lastCheckedName = $state('');
  let suggestionTimeout: ReturnType<typeof setTimeout> | null = null;

  const fetchSuggestion = async (query: string) => {
    if (query.length < 2) {
      suggestedName = null;
      lastCheckedName = query;
      suggestionPending = false;
      return;
    }
    suggestionPending = true;
    try {
      const result = await getSuggest(query, participantId);
      const suggested = result?.suggestions.at(0)?.name ?? null;
      suggestedName = suggested;
      lastCheckedName = query;
    } finally {
      suggestionPending = false;
    }
  };

  $effect(() => {
    const currentName = name.trim();
    if (currentName !== lastCheckedName) {
      suggestionPending = true;
    }

    if (suggestionTimeout) {
      clearTimeout(suggestionTimeout);
    }

    suggestionTimeout = setTimeout(() => {
      void fetchSuggestion(currentName);
    }, 1000);

    return () => {
      if (suggestionTimeout) {
        clearTimeout(suggestionTimeout);
      }
    };
  });

  const applySuggestion = () => {
    if (suggestedName) {
      name = suggestedName;
      suggestedName = null;
    }
  };

  /* "Fortsett med ..." submits straight away. Going through handleSubmit
     would ask the suggest API again and bring the same suggestion back. */
  const continueWithTypedName = () => {
    const trimmedName = name.trim();
    suggestedName = null;
    storeName(trimmedName);
    onSubmit(trimmedName);
  };

  const suggestionVisible = $derived(
    suggestedName != null && suggestedName !== name.trim()
  );
  const reduceMotion = () =>
    matchMedia('(prefers-reduced-motion: reduce)').matches;

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) return;

    if (suggestionTimeout) {
      clearTimeout(suggestionTimeout);
    }
    await fetchSuggestion(trimmedName);

    if (suggestedName && suggestedName !== trimmedName) {
      return;
    }

    storeName(trimmedName);
    onSubmit(trimmedName);
  };
</script>

<form
  onsubmit={(e) => {
    e.preventDefault();
    handleSubmit();
  }}
>
  <label class="input-label">
    Hvem er {maskName}?
    <input
      type="text"
      bind:value={name}
      placeholder="Skriv inn virkelig navn"
      class="name-input"
      autocomplete="off"
    />
  </label>
  <div class="actions">
    <button
      class="btn primary"
      type="submit"
      disabled={name.trim().length === 0 || suggestionVisible}
    >
      Neste
    </button>
    {#if suggestionPending && !suggestionVisible}
      <span class="status-text">Sjekker navn …</span>
    {/if}
  </div>
  {#if suggestionVisible}
    <div
      class="suggestion-area"
      transition:slide={{ duration: reduceMotion() ? 0 : 200 }}
    >
      <p class="suggestion-prompt">
        Mente du <strong>{suggestedName}</strong>?
      </p>
      <div class="suggestion-actions">
        <button type="button" class="btn primary" onclick={applySuggestion}>
          Bruk forslag
        </button>
        <button
          type="button"
          class="btn secondary"
          onclick={continueWithTypedName}
        >
          Fortsett med "{name.trim()}"
        </button>
      </div>
    </div>
  {/if}
</form>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .input-label {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .name-input {
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-base);
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius-md);
    background: var(--surface);
    color: var(--text);

    &:focus {
      outline: none;
      box-shadow: 0 0 0 var(--focus-ring-width) var(--accent);
    }
  }

  .status-text {
    margin: 0;
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .suggestion-area {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    background: light-dark(
      oklch(from var(--accent) 95% calc(c * 0.3) h),
      oklch(from var(--accent) 25% calc(c * 0.4) h)
    );
    border-radius: var(--radius-md);
    border: var(--border-width) solid var(--accent);
  }

  .suggestion-prompt {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text);

    & strong {
      color: var(--accent);
      font-weight: var(--nrk-sans-weight-bold);
    }
  }

  .suggestion-actions {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
</style>
